import { v4 as uuid } from 'uuid';

import { EQuestionType, EUnitSystem, IForm } from 'modules/forms/interfaces/form.interfaces';
import { EGender } from 'interfaces/patients.interfaces';

export const weightLossClinicForm: IForm = {
  title: 'Weight Loss Clinic',
  showPatientConsent: true,
  showShareWithGPConsent: true,
  sections: [
    {
      uuid: 'section0',
      title: 'General',
      showTitle: true,
      questions: [
        {
          uuid: uuid(),
          title: 'Are you under the age of 18 years or above the age of 85 years?',
          type: EQuestionType.BINARY,
          disqualification: {
            value: true,
          },
        },
      ],
    },
    {
      uuid: 'section1',
      title: 'General',
      showTitle: true,
      questions: [
        {
          uuid: uuid(),
          title:
            'Are you currently trying to become pregnant, or are you pregnant or breastfeeding?',
          type: EQuestionType.BINARY,
          disqualification: {
            value: true,
          },
        },
      ],
      conditions: [
        {
          type: 'customer',
          field: 'gender',
          operator: 'in',
          value: [EGender.FEMALE, EGender.NON_BINARY],
        },
      ],
    },
    {
      uuid: 'section2',
      title: 'Body Mass Index (BMI) measurement',
      showTitle: true,
      description: 'Please provide your most recent weight and height measurements:',
      questions: [
        {
          uuid: uuid(),
          type: EQuestionType.HEALTH_MEASUREMENT,
          value: {
            unitSystem: EUnitSystem.METRIC,
          },
        },
      ],
    },
    {
      uuid: 'section3',
      title: 'Existing medical conditions',
      questions: [
        {
          uuid: uuid(),
          title:
            'Do you currently have or have you ever had any of the following health conditions? Please select all that apply:',
          type: EQuestionType.CHECKBOXES,
          required: false,
          options: [
            'Type 2 diabetes',
            'High blood sugar (without diabetes) (Hyperglycemia)',
            'High blood pressure (hypertension)',
            'High cholesterol (dyslipidemia)',
            'Heart disease',
            'Sleep apnea',
            'Joint problems or osteoarthritis (especially knees or hips)',
            'Fatty liver disease',
            'Some types of cancer (e.g., breast, bowel)',
            'Polycystic ovary syndrome (PCOS)',
            'Inflammation of the pancreas (pancreatitis)',
            'Gallbladder issues',
          ],
        },
      ],
    },
    {
      uuid: 'section4',
      title: 'Weight Loss or Diabetes Treatments',
      questions: [
        {
          uuid: uuid(),
          title:
            'Have you ever been treated with any of the following medications for either weight loss or diabetes?',
          titleTable: {
            headers: [
              'Medication',
              'Brand Name(s)',
              'Category',
              'Mechanism of Action',
              'Primary Indication',
            ],
            rows: [
              [
                'Semaglutide',
                'Wegovy, Ozempic',
                'GLP-1 Receptor Agonist',
                'Mimics GLP-1 hormone to reduce appetite & delay gastric emptying',
                'Weight loss / Type 2 diabetes',
              ],
              [
                'Liraglutide',
                'Saxenda, Victoza',
                'GLP-1 Receptor Agonist',
                'Reduces hunger and slows digestion',
                'Weight loss / Type 2 diabetes',
              ],
              [
                'Tirzepatide',
                'Mounjaro',
                'GIP/GLP-1 Dual Agonist',
                'Enhances insulin secretion, reduces appetite',
                'Type 2 diabetes / Weight loss',
              ],
              [
                'Orlistat',
                'Xenical (Rx), Alli (OTC)',
                'Lipase Inhibitor',
                'Blocks absorption of dietary fat in the intestines',
                'Weight loss',
              ],
            ],
          },
          type: EQuestionType.RADIO,
          options: ['Yes', 'No'],
        },
        {
          uuid: uuid(),
          title: 'When was the most recent treatment and how long did it last?',
          placeholder: 'Free text',
          type: EQuestionType.SHORT_TEXT,
          conditions: [
            {
              field: 0,
              values: ['Yes'],
            },
          ],
        },
      ],
    },
    {
      uuid: 'section5',
      title: 'Allergies & Sensitivity Screening',
      questions: [
        {
          uuid: uuid(),
          title: 'Medication Allergy Screening',
          description: (
            <>
              Have you ever had an allergic reaction or sensitivity (mild or severe) to any of the
              following weight loss medications or their ingredients?
              <div style={{ marginTop: '4px' }}>
                Wegovy (Semaglutide), Ozempic, Saxenda (Liraglutide), Victoza, Mounjaro
                (Tirzepatide), Orlistat (Xenical or Alli)
              </div>
            </>
          ),
          type: EQuestionType.RADIO,
          options: ['Yes', 'No'],
        },
        {
          uuid: uuid(),
          title: 'Please describe your reaction and the medication involved',
          type: EQuestionType.SHORT_TEXT,
          placeholder: 'Free text',
          conditions: [
            {
              field: 0,
              values: ['Yes'],
            },
          ],
        },
        {
          uuid: uuid(),
          title: 'Injection-Related Sensitivities',
          description: (
            <>
              Do you have any known allergies or unusual reactions to injectable medications or
              ingredients, including:
              <ul>
                <li>Peptide or protein-based drugs</li>
                <li>Polyethylene glycol (PEG)</li>
                <li>Preservatives (e.g., phenol, sodium chloride)</li>
                <li>Penicillin or other injectable drugs (e.g., amoxicillin, cephalexin)</li>
                <li>Dyes or capsule fillers</li>
              </ul>
              OR
              <br />
              Have you experienced any of the following after using injections?
              <ul>
                <li>Redness, swelling, or rash at the injection site</li>
                <li>Difficulty breathing, hives, or facial swelling</li>
                <li>Unexplained nausea, vomiting, or dizziness</li>
              </ul>
            </>
          ),
          type: EQuestionType.RADIO,
          options: ['Yes', 'No'],
        },
        {
          uuid: uuid(),
          title: 'Please describe your symptoms and when they occurred',
          type: EQuestionType.SHORT_TEXT,
          placeholder: 'Free text',
          conditions: [
            {
              field: 2,
              values: ['Yes'],
            },
          ],
        },
        {
          uuid: uuid(),
          title: 'Do you have any food allergies?',
          description: `While food allergies don't directly affect these medications, they may indicate a higher risk for sensitivity or autoimmune conditions.`,
          type: EQuestionType.RADIO,
          options: ['Yes', 'No'],
        },
        {
          uuid: uuid(),
          title: 'Please specify the allergies you have',
          type: EQuestionType.SHORT_TEXT,
          placeholder: 'Free text',
          conditions: [
            {
              field: 4,
              values: ['Yes'],
            },
          ],
        },
      ],
    },
    {
      uuid: 'submit',
      title: 'Thank you for taking the time to complete this form.',
      questions: [],
    },
  ],
};
